import React, { useEffect, useRef } from 'react';
import { Dayjs } from 'dayjs';
import styled from 'styled-components';

import { TAB_HEIGHT } from '@/features/canvas/constants';
import { FilteredNodes } from '@/features/canvas/contexts/editor-context';

import { InitiativeBar } from './InitiativeBar';

// Constants matching ACTUAL Gantt bar measurements with consistent padding
const GANTT_PADDING_ABOVE = 6; // Consistent padding above each bar

// Variable heights for parent vs child rows
const GANTT_PARENT_HEIGHT = 28; // Height for parent rows
const GANTT_CHILD_HEIGHT = 20; // Height for child rows (more compact)

// Only the initial top padding, no additional section padding
const GANTT_SECTION_PADDING = GANTT_PADDING_ABOVE; // Only initial top padding

// Helper function to get row height based on depth
const getRowHeight = (depth: number) => (depth > 0 ? GANTT_CHILD_HEIGHT : GANTT_PARENT_HEIGHT);

// Helper function to calculate cumulative position
const calculateCumulativePosition = (items: { depth: number }[], index: number) => {
  let position = GANTT_SECTION_PADDING;
  for (let i = 0; i < index; i += 1) {
    position += getRowHeight(items[i].depth);
  }
  return position;
};

type Props = {
  allInitiatives: FilteredNodes[];
  getSectionHeight: (sectionId: number) => number;
  initiatives: FilteredNodes[];
  isDependencyMode: boolean;
  isLastSection: boolean;
  nodeCount: number;
  sectionId: number;
  timelineEnd: Dayjs;
  timelineStart: Dayjs;
  connectedInitiativeIds?: Set<number>;
  // Tree structure with depth info
  initiativesWithDepth?: { depth: number; node: FilteredNodes }[];
  isIsolationActive?: boolean;
  isolatedInitiativeId?: number | null;
  // Gantt-specific isolation props
  onBarClick?: (initiativeId: number) => void;
};

const GanttTimelineSection = ({
  allInitiatives,
  initiatives,
  timelineStart,
  timelineEnd,
  nodeCount,
  isLastSection,
  getSectionHeight,
  sectionId,
  isDependencyMode,
  onBarClick,
  isolatedInitiativeId,
  connectedInitiativeIds = new Set(),
  isIsolationActive = false,
  initiativesWithDepth = [],
}: Props) => {
  const sectionRef = useRef<HTMLDivElement>(null);

  // Calculate section height for use in JSX and useEffect
  const SWIMLANE_MIN_HEIGHT = 97.06;
  // Calculate total height based on variable row heights
  const calculatedHeight =
    nodeCount > 0
      ? initiativesWithDepth.reduce((sum, item) => sum + getRowHeight(item.depth), 0) +
        GANTT_SECTION_PADDING
      : SWIMLANE_MIN_HEIGHT;
  const sidebarHeight = getSectionHeight(sectionId);
  const finalSectionHeight = Math.max(calculatedHeight, sidebarHeight, SWIMLANE_MIN_HEIGHT);

  useEffect(() => {
    if (sectionRef.current) {
      sectionRef.current.style.minHeight = `${finalSectionHeight}px`;
    }
  }, [finalSectionHeight]);

  return (
    <SectionRow ref={sectionRef}>
      <InitiativesContainer>
        {initiatives.map((node, initiativeIndex) => {
          const initiativeId = node.initiatives?.id || node.id;
          const isIsolated = isolatedInitiativeId === initiativeId;
          const isConnectedToIsolated = initiativeId
            ? connectedInitiativeIds.has(initiativeId)
            : false;

          // Get depth from initiativesWithDepth array
          const depth = initiativesWithDepth[initiativeIndex]?.depth || 0;

          // Calculate cumulative position for this item
          const topPosition = calculateCumulativePosition(initiativesWithDepth, initiativeIndex);

          return (
            <InitiativeBar
              key={node.id}
              allInitiatives={allInitiatives}
              node={node}
              timelineStart={timelineStart}
              timelineEnd={timelineEnd}
              initiativeIndex={initiativeIndex}
              topPosition={topPosition}
              isDependencyMode={isDependencyMode}
              onBarClick={onBarClick}
              isIsolated={isIsolated}
              isConnectedToIsolated={isConnectedToIsolated}
              isIsolationActive={isIsolationActive}
              depth={depth}
              rowHeight={getRowHeight(depth)}
            />
          );
        })}
        {/* Add horizontal lines above and between initiatives */}
        {initiatives.length > 0 && (
          <>
            {/* Line above first bar */}
            <HorizontalLine key="line-top" top={0} />
            {/* Lines between bars - positioned using cumulative heights, but skip between parent-child and child-child */}
            {Array.from({ length: Math.max(0, initiatives.length - 1) }, (_, index) => {
              const currentDepth = initiativesWithDepth[index]?.depth || 0;
              const nextDepth = initiativesWithDepth[index + 1]?.depth || 0;

              // Skip line if current or next item is a child (depth > 0)
              if (currentDepth > 0 || nextDepth > 0) {
                return null;
              }

              // Calculate position after current item
              const linePosition = calculateCumulativePosition(initiativesWithDepth, index + 1);
              return <HorizontalLine key={`line-${index}`} top={linePosition} />;
            })}
            {/* Line underneath last initiative when section doesn't fill minimum height */}
            {initiatives.length > 0 && calculatedHeight < finalSectionHeight && (
              <HorizontalLine
                key="line-after-last"
                top={calculateCumulativePosition(initiativesWithDepth, initiatives.length)}
              />
            )}
            {/* Bottom line for the last section */}
            {isLastSection && (
              <HorizontalLine
                key="line-bottom"
                top={calculateCumulativePosition(initiativesWithDepth, initiatives.length)}
              />
            )}
            {/* Bottom line for sections that don't fill minimum height (except last section) */}
            {!isLastSection && calculatedHeight < finalSectionHeight && (
              <HorizontalLine key="line-section-bottom" top={finalSectionHeight} />
            )}
          </>
        )}
        {/* Add bottom line for empty sections (except last section) */}
        {initiatives.length === 0 && !isLastSection && (
          <HorizontalLine key="empty-section-line" top={finalSectionHeight} />
        )}
      </InitiativesContainer>
    </SectionRow>
  );
};

const SectionRow = styled.div`
  min-height: ${TAB_HEIGHT}px;
  position: relative;
  z-index: 1;
  background: transparent;

  /* Ensure no inherited background patterns */
  background-image: none;
  background-size: auto;
  background-repeat: no-repeat;

  /* Explicitly prevent any borders or lines */
  border: none;
  outline: none;
  box-shadow: none;
`;

const InitiativesContainer = styled.div`
  position: relative;
  height: 100%;
  min-width: 100%;

  /* Clean container with no background patterns */
  background: transparent;
  background-image: none;
  border: none;
`;

const HorizontalLine = styled.div<{ top: number }>`
  position: absolute;
  top: ${({ top }) => top}px;
  left: 0;
  right: 0;
  height: 1px;
  background-color: #dfdfdf;
  z-index: 1;
`;

export { GanttTimelineSection };
